import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';

const SortableRow = ({ type, entryKey, index, disabled, onHover, onDropped, render }) => {
  const rowRef = useRef(null);
  const handleRef = useRef(null);

  const [, drop] = useDrop({
    accept: type,
    hover(dragged, monitor) {
      if (!rowRef.current || dragged.index === index) {
        return;
      }
      const box = rowRef.current.getBoundingClientRect();
      const middle = (box.bottom - box.top) / 2;
      const offset = monitor.getClientOffset().y - box.top;
      // Only swap once the pointer has crossed the middle of the row it is over.
      if ((dragged.index < index && offset < middle) || (dragged.index > index && offset > middle)) {
        return;
      }
      onHover(dragged.index, index);
      dragged.index = index;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: () => ({ key: entryKey, index, startIndex: index }),
    canDrag: !disabled,
    end: (dragged) => onDropped(dragged.startIndex, dragged.index),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(handleRef);
  drop(preview(rowRef));

  return render({ rowRef, handleRef, isDragging });
};

/**
 * A list reordered by dragging a handle. The order changes locally while dragging and `onReorder`
 * runs once, on drop, with the start and end positions, so a drag costs one save rather than one per
 * row crossed.
 */
const SortableList = ({ type, items, getKey, onReorder, disabled, renderItem }) => {
  const [order, setOrder] = useState(items);
  const itemsKey = items.map(getKey).join('|');

  useEffect(() => {
    setOrder(items);
    // Re-sync only when the set or order of entries changes, not on every new array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, items]);

  const onHover = (from, to) => {
    setOrder((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onDropped = (from, to) => {
    if (from !== to) {
      onReorder(from, to);
    }
  };

  return order.map((item, index) => (
    <SortableRow
      key={getKey(item)}
      type={type}
      entryKey={getKey(item)}
      index={index}
      disabled={disabled}
      onHover={onHover}
      onDropped={onDropped}
      render={(drag) => renderItem(item, index, drag)}
    />
  ));
};

SortableList.propTypes = {
  type: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  getKey: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  renderItem: PropTypes.func.isRequired,
};

export default SortableList;
